import { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundaryがエラーをキャッチしました:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-content">
              <h2 className="error-boundary-title">⚠️ エラーが発生しました</h2>
              <p className="error-boundary-message">
                申し訳ございません。予期しないエラーが発生しました。
              </p>
              
              <div className="error-boundary-actions">
                <button 
                  className="error-boundary-btn primary"
                  onClick={this.handleReset}
                >
                  もう一度試す
                </button>
                <button 
                  className="error-boundary-btn secondary"
                  onClick={this.handleReload}
                >
                  ページを再読み込み
                </button>
              </div>

              {import.meta.env.DEV && this.state.error && (
                <details className="error-boundary-details">
                  <summary>開発者向け詳細情報</summary>
                  <div className="error-boundary-debug">
                    <h4>エラーメッセージ:</h4>
                    <pre className="error-boundary-code">
                      {this.state.error.message}
                    </pre>
                    
                    <h4>スタックトレース:</h4>
                    <pre className="error-boundary-code">
                      {this.state.error.stack}
                    </pre>
                    
                    {this.state.errorInfo && (
                      <>
                        <h4>コンポーネントスタック:</h4>
                        <pre className="error-boundary-code">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;